export interface Creative {
    id: string;
    src: string;
    label: string;
    recommendedFormat: "square" | "story" | "banner";
    width: number;
    height: number;
    description?: string;
    language?: string;
}

export type CreativeLanguage = "es" | "en" | "fr" | "ca" | "it" | "pt";

export const supportedCreativeLanguages: CreativeLanguage[] = [
    "es",
    "en",
    "fr",
    "ca",
    "it",
    "pt",
];

function isSupportedLanguage(lang: string): lang is CreativeLanguage {
    return (supportedCreativeLanguages as string[]).includes(lang);
}

/**
 * Creatividades disponibles por idioma.
 *
 * Estructura de assets:
 * - public/assets/img/creatividades/{lang}/post_01.png
 * - public/assets/img/creatividades/{lang}/story_01.png
 */
function pad2(n: number): string {
    return String(n).padStart(2, "0");
}

function buildStandardCreatives(lang: CreativeLanguage): Creative[] {
    const list: Creative[] = [];

    // Posts (cuadrados)
    for (let i = 1; i <= 5; i++) {
        const num = pad2(i);
        list.push({
            id: `${lang}_post_${num}`,
            src: `/assets/img/creatividades/${lang}/post_${num}.png`,
            label: `Post ${num}`,
            recommendedFormat: "square",
            width: 1080,
            height: 1080,
            language: lang,
        });
    }

    // Stories (vertical)
    for (let i = 1; i <= 9; i++) {
        const num = pad2(i);
        list.push({
            id: `${lang}_story_${num}`,
            src: `/assets/img/creatividades/${lang}/story_${num}.png`,
            label: `Story ${num}`,
            recommendedFormat: "story",
            width: 1080,
            height: 1920,
            language: lang,
        });
    }

    return list;
}

const creativesByLanguage: Record<CreativeLanguage, Creative[]> = {
    es: buildStandardCreatives("es"),
    en: buildStandardCreatives("en"),
    fr: buildStandardCreatives("fr"),
    ca: buildStandardCreatives("ca"),
    it: buildStandardCreatives("it"),
    pt: buildStandardCreatives("pt"),
};

export function normalizeCreativeLanguage(input: string): CreativeLanguage {
    const lang = (input || "").toLowerCase();

    // Map app languages to available creative languages
    if (lang === "eslat") return "es";
    if (lang.startsWith("es")) return "es";
    if (lang.startsWith("en")) return "en";
    if (lang.startsWith("fr")) return "fr";
    if (lang.startsWith("ca")) return "ca";
    if (lang.startsWith("it")) return "it";
    if (lang.startsWith("pt")) return "pt";

    return isSupportedLanguage(lang) ? lang : "es";
}

export function getCreativesManifest(language: string): Creative[] {
    const lang = normalizeCreativeLanguage(language);
    const list = creativesByLanguage[lang];

    // Fallback: if language folder has no entries yet, show ES templates.
    if (!list || list.length === 0) return creativesByLanguage.es;
    return list;
}

export function getCreativeById(language: string, id: string): Creative | undefined {
    return getCreativesManifest(language).find((c) => c.id === id);
}

export function getCreativesByFormat(
    language: string,
    format: "square" | "story" | "banner",
): Creative[] {
    return getCreativesManifest(language).filter(
        (c) => c.recommendedFormat === format,
    );
}

export function getFormatIcon(format: "square" | "story" | "banner"): string {
    const icons = {
        square: "⬜",
        story: "📱",
        banner: "🖼️",
    } as const;

    return icons[format] || "🖼️";
}
